import {
  AHSeparator,
  AHText,
  AHTextFonts,
  AHTodoCategoryIcon,
  Box,
  ListItem,
  useTheme,
} from "arkh";
import React, { useCallback, useMemo } from "react";

import { TipArea } from "../../components";

import { FormattedMessage } from "react-intl";
import { useIntlTokens } from "../../components/mobile/useIntlTokens";
import { graphql, useLazyLoadQuery } from "react-relay";
import { TodoCategoryAdminListQuery } from "./__generated__/TodoCategoryAdminListQuery.graphql";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { StoreAdminNavigationScreenProps } from "../StoreAdmin/StoreAdminNavigation";
import { ActionFooter } from "@/src/components/ActionFooter";
import { FlatList, View } from "react-native";

type TodoCategoryAdminListProps =
  StoreAdminNavigationScreenProps<"TodoCategoryAdminList">;

export function TodoCategoryAdminList(props: TodoCategoryAdminListProps) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => <InnerTodoCategoryAdminList {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerTodoCategoryAdminList({
  navigation,
  fetchKey,
}: TodoCategoryAdminListProps & { fetchKey: string }) {
  const intl_tokens = useIntlTokens();

  const data = useLazyLoadQuery<TodoCategoryAdminListQuery>(
    graphql`
      query TodoCategoryAdminListQuery {
        store {
          id
          todoCategories {
            id
            title
            icon
          }
        }
      }
    `,
    {},
    { fetchKey, fetchPolicy: "store-and-network" }
  );
  const categories = useMemo(
    () => data?.store?.todoCategories ?? [],
    [data?.store?.todoCategories]
  );

  const renderItem = useCallback(
    ({ item, index }: { item: (typeof categories)[number]; index: number }) => {
      if (!item) return null;
      return (
        <ListItem
          onPress={() => {
            navigation.navigateDeprecated("TodoCategoryEdit", {
              id: item.id,
            });
          }}
          left={item.icon ? <AHTodoCategoryIcon icon={item.icon} /> : null}
          right={<ListItem.NavArrow />}
        >
          <ListItem.Title>{item.title}</ListItem.Title>
        </ListItem>
      );
    },
    [navigation]
  );

  const theme = useTheme();
  return (
    <View style={{ flex: 1 }}>
      <FlatList
        contentContainerStyle={{
          flex: 1,
          backgroundColor: theme["surface-background-light"],
        }}
        ListHeaderComponent={
          <>
            <Box space="medium" color="surface">
              <TipArea>
                <AHText color="text-primary" type="body">
                  <AHText
                    font={AHTextFonts.BOLD}
                    color="text-primary"
                    type="body"
                  >
                    {intl_tokens.TipTitle}{" "}
                  </AHText>
                  <FormattedMessage
                    defaultMessage="Some things are better kept in a separate list. Here you can create custom lists for different kinds of to-do's, for example Improvements, Customer Requests or Long-haul tasks."
                    description="TodoCategoryAdminList tip"
                  />
                </AHText>
              </TipArea>
            </Box>
            <AHSeparator />
          </>
        }
        contentInsetAdjustmentBehavior="automatic"
        data={categories}
        renderItem={renderItem}
        ItemSeparatorComponent={AHSeparator}
        ListFooterComponent={AHSeparator}
      />

      <ActionFooter
        safeInsetBottom
        primary={
          <ActionFooter.Primary
            title={intl_tokens.CreateCustomList}
            icon="add"
            onPress={() => {
              navigation.navigateDeprecated("TodoCategoryCreate", {});
            }}
          />
        }
      />
    </View>
  );
}
